import { describe_error } from '../../utils/ErrorDescriber';
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';

import Constants from '../../Constants';
import { useAuth } from '../../AuthContext';
import { get_user_error_message } from '../../utils/ErrorMessages';
import { format_cents } from '../../utils/Money';
import { get_billing_interval_label } from '../subscriptions/BillingIntervals';

// Invites a user, for the superadmin page: their email, name and the subscription plan they start on, chosen from the
// active plans, which load when the modal opens. on_invite(invite_data) returns a promise and rejects with an Error
// carrying the message to show, so the modal stays open with the error. on_close closes it.
function InviteUserModal({on_invite, on_close}){
	const { token } = useAuth();

	const [email, set_email] = useState('');
	const [firstname, set_firstname] = useState('');
	const [lastname, set_lastname] = useState('');
	const [plan_id, set_plan_id] = useState('');

	// The active plans to choose from: null until they have loaded.
	const [plans, set_plans] = useState(null);
	const [plans_error, set_plans_error] = useState('');

	const [sending, set_sending] = useState(false);
	const [error, set_error] = useState('');

	const mounted_ref = useRef(true);

	const get_headers = ()=>{
		return { Authorization: 'Bearer ' + token };
	};

	const load_plans = async()=>{
		set_plans_error('');

		try {
			const response = await axios.get(Constants.SERVER_URL + '/api/admin/subscription_plans', { headers: get_headers() });
			if(!mounted_ref.current){
				return;
			}

			const active_plans = response.data.plans.filter((plan)=>{ return plan.is_active; });
			set_plans(active_plans);
		}
		catch (error) {
			console.log('InviteUserModal/load_plans - ERROR: ', describe_error(error));
			if(mounted_ref.current){
				set_plans_error(get_user_error_message(error, 'The subscription plans could not be loaded.'));
				set_plans([]);
			}
		}
	};

	useEffect(()=>{
		mounted_ref.current = true;
		load_plans();

		return ()=>{
			mounted_ref.current = false;
		};
	}, [token]);

	// A plan as the dropdown names it: its name, then its price per billing interval.
	const get_plan_label = (plan)=>{
		return plan.name + ', ' + format_cents(plan.price_in_cents, plan.currency) + ' per ' + get_billing_interval_label(plan.billing_interval);
	};

	// The invite as the backend takes it, or null with the error shown. The email check here is only the obvious
	// one; the backend checks the address properly.
	const get_invite_data = ()=>{
		const trimmed_email = email.trim();
		if(trimmed_email.length === 0){
			set_error('Email is required.');
			return null;
		}

		if(!trimmed_email.includes('@')){
			set_error('Enter a valid email address.');
			return null;
		}

		if(plan_id.length === 0){
			set_error('Choose a subscription plan.');
			return null;
		}

		return {
			email: trimmed_email,
			firstname: firstname.trim(),
			lastname: lastname.trim(),
			plan_id
		};
	};

	const handle_send = async(event)=>{
		event.preventDefault();

		const invite_data = get_invite_data();
		if(!invite_data){
			return;
		}

		set_error('');
		set_sending(true);

		try {
			await on_invite(invite_data);
		}
		catch (error) {
			console.log('InviteUserModal/handle_send - ERROR: ', describe_error(error));
			if(mounted_ref.current){
				set_error(error.message);
				set_sending(false);
			}
		}
	};

	const render_send_label = ()=>{
		if(sending){
			return (
				<Spinner animation="border" size="sm"/>
			);
		}

		return 'Send Invite';
	};

	// The plan dropdown: a spinner while the plans load, an error when they could not, else the active plans.
	const render_plan_field = ()=>{
		if(plans === null){
			return (
				<div className="invite-user-plans-loading">
					<Spinner animation="border" size="sm"/>
				</div>
			);
		}

		if(plans_error){
			return <Alert variant="danger">{plans_error}</Alert>;
		}

		if(plans.length === 0){
			return <p className="invite-user-note">There are no active subscription plans to invite the user to.</p>;
		}

		return (
			<Form.Select
				value={plan_id}
				disabled={sending}
				onChange={(event)=>{ set_plan_id(event.target.value); }}>
				<option value="">Choose a plan</option>
				{plans.map((plan)=>{
					return <option key={plan.id} value={plan.id}>{get_plan_label(plan)}</option>;
				})}
			</Form.Select>
		);
	};

	return (
		<Modal show={true} onHide={on_close} centered>
			<Modal.Header closeButton>
				<Modal.Title>Invite User</Modal.Title>
			</Modal.Header>

			<Form onSubmit={handle_send}>
				<Modal.Body>
					{error && <Alert variant="danger">{error}</Alert>}

					<p className="invite-user-note">
						The user is emailed a link to set their password. They can log in once they have set it.
					</p>

					<Form.Group className="subscription-modal-field">
						<Form.Label>Email</Form.Label>
						<Form.Control
							type="text"
							value={email}
							disabled={sending}
							onChange={(event)=>{ set_email(event.target.value); }}/>
					</Form.Group>

					<div className="subscription-modal-row">
						<Form.Group className="subscription-modal-field">
							<Form.Label>First name</Form.Label>
							<Form.Control
								type="text"
								value={firstname}
								disabled={sending}
								onChange={(event)=>{ set_firstname(event.target.value); }}/>
						</Form.Group>

						<Form.Group className="subscription-modal-field">
							<Form.Label>Last name</Form.Label>
							<Form.Control
								type="text"
								value={lastname}
								disabled={sending}
								onChange={(event)=>{ set_lastname(event.target.value); }}/>
						</Form.Group>
					</div>

					<Form.Group className="subscription-modal-field">
						<Form.Label>Subscription plan</Form.Label>
						{render_plan_field()}
					</Form.Group>
				</Modal.Body>

				<Modal.Footer>
					<Button variant="secondary" type="button" disabled={sending} onClick={on_close}>
						Cancel
					</Button>
					<Button variant="primary" type="submit" disabled={sending || (plans === null)}>
						{render_send_label()}
					</Button>
				</Modal.Footer>
			</Form>
		</Modal>
	);
}

export default InviteUserModal;
